<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { userModule } from '@/store/user/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import UIKit from '@pocketprep/ui-kit'
import * as Sentry from '@sentry/browser'
import { bundleModule } from '@/store/bundle/module'
import { subscriptionModule } from '@/store/subscription/module'
import { examMetadataModule } from '@/store/examMetadata/module'

const Calendar = UIKit.Calendar
const ModalContainer = UIKit.ModalContainer
const Modal = UIKit.Modal
const PocketButton = UIKit.Button

const emit = defineEmits<{
    close: []
}>()

const examDate = ref<Date | null>(null)
const isLoading = ref(true)
const hasExamDateLoaded = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const currentUserExamMetadata = computed(() => {
    return userExamMetadataModule.getters.getCurrentUserExamMetadata()
})

const bundle = computed(() => {
    return bundleModule.getters.getCurrentBundle()
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const hasExamDateChanged = computed(() => {
    const uem = currentUserExamMetadata.value
    const existingExamDate = uem?.scheduledExamDate
    if (existingExamDate && examDate.value) {
        return new Date(existingExamDate.iso).getTime() !== examDate.value.getTime()
    } else if (!existingExamDate && !examDate.value) {
        return false
    } else {
        return true
    }
})

const hasSubscription = computed(() => {
    return !!subscriptionModule.getters.getSubscriptionForExamId(currentUserExamMetadata.value?.objectId)
})

onMounted(async () => {
    await userModule.actions.fetchUserData()
    const uem = userExamMetadataModule.getters.getCurrentUserExamMetadata()
    if (uem?.scheduledExamDate) {
        examDate.value = new Date(uem.scheduledExamDate.iso)
    }
    hasExamDateLoaded.value = true
    isLoading.value = false

    setTimeout(() => {
        const titleEl: HTMLElement | null = document.querySelector('.exam-date__title')
        titleEl?.focus()
    }, 0)
})

const saveExamDate = async () => {
    if (!hasExamDateChanged.value) {
        return
    }

    isLoading.value = true
    const date = examDate.value || null

    // if date is somehow not valid, capture date and send to Sentry
    if (date && isNaN(date.getTime())) {
        Sentry.captureException(new Error(`Invalid date: ${examDate.value}`))
    } else {
        if (currentUserExamMetadata.value && bundle.value && currentExamMetadata.value) {
            await userExamMetadataModule.actions.setScheduledExamDate({
                scheduledExamDate: date,
                examNativeName: currentExamMetadata.value.nativeAppName,
                uemCreatedAt: currentUserExamMetadata.value.createdAt,
                uemObjectId: currentUserExamMetadata.value.objectId,
                bundle: bundle.value,
                hasSubscription: hasSubscription.value,
            })
        }
    }

    isLoading.value = false
    emitClose()
}

const emitClose = () => {
    emit('close')
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="examDateModal"
            class="exam-date"
            :is-dark-mode="isDarkMode"
            @close="emitClose"
        >
            <template #modal>
                <Modal
                    :is-dark-mode="isDarkMode"
                    class="exam-date__modal"
                    :show-close-button="true"
                    @close="emitClose"
                >
                    <div tabindex="-1" class="exam-date__title">
                        Set Your Exam Date
                    </div>
                    <Calendar
                        v-if="hasExamDateLoaded"
                        v-model="examDate"
                        class="exam-date__calendar"
                        label="Scheduled Exam Date"
                        :is-dark-mode="isDarkMode"
                        @keydown.enter="!isLoading && examDate && saveExamDate()"
                    />
                    <div class="exam-date__buttons">
                        <PocketButton
                            :is-dark-mode="isDarkMode"
                            type="secondary"
                            @click="emitClose"
                        >
                            Cancel
                        </PocketButton>
                        <PocketButton
                            :is-dark-mode="isDarkMode"
                            :is-loading="isLoading"
                            :disabled="!hasExamDateChanged"
                            @click="saveExamDate"
                        >
                            Save Date
                        </PocketButton>
                    </div>
                </Modal>
            </template>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.exam-date {
    color: $brand-black;
    z-index: 9998;

    &__modal {
        padding: 35px 25px 22px;
        width: 424px;
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 80px;
        max-width: 95%;
        box-sizing: border-box;

        @include breakpoint(black-bear) {
            top: 50%;
            transform: translate(-50%, -50%);
        }
    }

    &__title {
        margin-bottom: 38px;
        font-size: 18px;
        line-height: 22px;
        font-weight: 600;
        text-align: center;
        outline: none;
    }

    &__calendar {
        margin-bottom: 41px;
    }

    &__buttons {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 220px;
        margin: 0 auto;
    }
}
</style>
